import BaseApi from '@core/api/model';

import { wait } from '@utils/async';

import RatePlanEntry from '@/models/RatePlanEntry';

export class RatePlanApi extends BaseApi {
    static resource = 'rate-plans';
    static mockItemCount = 6;
    static mockUseFilterOverrides = true;
    static mockFilterOverrideKeys = ['adviser_id', 'type', 'status'];

    get endpoints() {
        return {
            ...super.endpoints,
            getEntries: this.getEntries.bind(this),
            getEntry: this.getEntry.bind(this)
        };
    }

    get mock_endpoints() {
        return {
            ...super.mock_endpoints,
            getEntries: this.getEntries_mock.bind(this),
            getEntry: this.getEntry_mock.bind(this)
        };
    }

    _normaliseRatePlanItem(item = {}) {
        if (!item || typeof item !== 'object') {
            return item;
        }

        return {
            ...item,
            default: Boolean(item.default)
        };
    }

    _normaliseRatePlanData(data) {
        if (Array.isArray(data)) {
            return data.map(item => this._normaliseRatePlanItem(item));
        }

        return this._normaliseRatePlanItem(data);
    }

    _filterGetResponseData(data) {
        return this._normaliseRatePlanData(data);
    }

    _filterIndexResponseData(data) {
        return this._normaliseRatePlanData(data);
    }

    async getEntries(ratePlanId, params = {}, config = {}) {
        if (!ratePlanId) {
            throw new ReferenceError('ratePlanId is required when requesting rate plan entries');
        }

        const response = await BaseApi.api.get(`${this.base_url}/${ratePlanId}/entries`, params, config);

        return this._respond(response, () => {
            response.data = this._filterResponseData(response.data);
            this._insertModels(response.data, RatePlanEntry);
        });
    }

    async getEntry(ratePlanId, entryId, params = {}, config = {}) {
        if (!ratePlanId) {
            throw new ReferenceError('ratePlanId is required when requesting a rate plan entry');
        }

        if (!entryId) {
            throw new ReferenceError('entryId is required when requesting a rate plan entry');
        }

        const response = await BaseApi.api.get(`${this.base_url}/${ratePlanId}/entries/${entryId}`, params, config);

        return this._respond(response, () => {
            response.data = this._filterResponseData(response.data);
            this._insertModel(response.data, this.ApiResponseFormatEnum.DETAIL, RatePlanEntry);
        });
    }

    async getEntries_mock(ratePlanId) {
        if (!ratePlanId) {
            throw new ReferenceError('ratePlanId is required when requesting rate plan entries');
        }

        await wait(200);

        const response = {
            status: 'success',
            data: [
                {
                    id: `${ratePlanId}-entry-1`,
                    rate_plan_id: ratePlanId,
                    type: 'PLATFORM',
                    from_amount: { amount: 0, currency: 'GBP' },
                    to_amount: null,
                    from_quantity: 0,
                    to_quantity: null,
                    rate: 0.25,
                    charge: null,
                    split_rates: false,
                    eis_rate: null,
                    vct_rate: null,
                    iht_rate: null,
                    pmi_rate: null
                }
            ]
        };

        return this._respond(response, () => {
            response.data = this._filterResponseData(response.data);
            this._insertModels(response.data, RatePlanEntry);
        });
    }

    async getEntry_mock(ratePlanId, entryId) {
        const response = await this.getEntries_mock(ratePlanId);
        const entry = (response.data || []).find(item => item.id === entryId) || response.data?.[0] || null;

        return {
            ...response,
            data: entry
        };
    }

    // eslint-disable-next-line no-unused-vars
    async index_mock(offset = 0, limit = 100, sort_by = null, filters = {}) {
        await wait(200);

        let items = this._getMocked();

        items = items.filter(item => {
            if (this.constructor.mockSoftDelete && item.deleted_at) {
                return false;
            }

            return true;
        });

        if (!items.length) {
            items = await this._generateMocked(this.constructor.mockItemCount);
        }

        items = this._normaliseRatePlanData(items);

        let filtered = this._filterMocked(items, filters);

        if (this.constructor.mockUseFilterOverrides && !filtered.length && filters && Object.keys(filters).length) {
            const overrides = {};

            for (const key in filters) {
                const value = filters[`${key}`];

                if (
                    this.constructor.mockFilterOverrideKeys.includes(key) &&
                    typeof value === 'string' &&
                    value.length
                ) {
                    const parts = value.split(':');
                    overrides[`${key}`] = parts.length > 1 ? parts.slice(1).join(':') : value;
                }
            }

            if (Object.keys(overrides).length) {
                filtered = this._normaliseRatePlanData(
                    await this._generateMocked(this.constructor.mockItemCount, overrides)
                );
            }
        }

        items = filtered;

        const sliced = offset !== null && limit !== null ? items.slice(offset, offset + limit) : items;

        const response = {
            status: 'success',
            data: sliced,
            metadata: {
                total: items.length,
                limit,
                offset
            }
        };

        return this._respond(response, () => {
            this._insertModels(response.data);
        });
    }
}

export default RatePlanApi;
